import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  Divider,
  Chip
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { authGet, authPost, authPatch, authDelete } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { formatDate } from '../../lib/dateFormat';

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}


function formatTimeLabel(time24) {
  if (!time24) return '';
  const [hourStr, minute] = String(time24).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

export default function InstructorsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [instructorClasses, setInstructorClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    code: '',
    name: '',
    active: true,
    phone: '',
    email: ''
  });

  const loadInstructors = async () => {
    setLoading(true);
    try {
      const data = await authGet('/api/instructors');
      setRows(data.instructors || []);
    } catch (err) {
      console.error('Load instructors error:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstructors();
  }, []);

  const resetForm = () => {
    setForm({
      code: '',
      name: '',
      active: true,
      phone: '',
      email: ''
    });
    setEditing(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        code: form.code?.trim() || '',
        name: form.name?.trim() || '',
        phone: formatPhone(form.phone),
        email: form.email?.trim() || ''
      };

      if (!payload.name) {
        alert('Name is required.');
        return;
      }

      if (editing) {
        await authPatch(`/api/instructors/${editing.id}`, payload);
      } else {
        await authPost('/api/instructors', payload);
      }

      setOpen(false);
      resetForm();
      await loadInstructors();
    } catch (err) {
      console.error('Save instructor error:', err);
      alert(err.message || 'Unable to save instructor');
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code || '',
      name: row.name || '',
      active: Boolean(row.active),
      phone: row.phone || '',
      email: row.email || ''
    });
    setOpen(true);
  };

  const handleView = async (row) => {
    try {
      const data = await authGet(`/api/instructors/${row.id}`);
      setSelectedInstructor(data.instructor || null);
      setInstructorClasses(data.classes || []);
      setDetailOpen(true);
    } catch (err) {
      console.error('Load instructor detail error:', err);
      alert(err.message || 'Unable to load instructor detail');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this instructor?')) return;

    try {
      await authDelete(`/api/instructors/${id}`);
      await loadInstructors();
    } catch (err) {
      console.error('Delete instructor error:', err);
      alert(err.message || 'Unable to delete instructor');
    }
  };

  const displayRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        phone_display: formatPhone(row.phone),
        active_display: row.active ? 'True' : 'False'
      })),
    [rows]
  );

  const columns = [
    { field: 'code', headerName: 'Code', flex: 0.8 },
    { field: 'name', headerName: 'Name', flex: 1.8 },
    { field: 'active_display', headerName: 'Active', flex: 0.9 },
    { field: 'phone_display', headerName: 'Phone', flex: 1.2 },
    { field: 'email', headerName: 'Email', flex: 1.5 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 260,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
          <Button size="small" variant="outlined" onClick={() => handleView(params.row)}>
            VIEW
          </Button>

          <Button size="small" variant="outlined" onClick={() => handleEdit(params.row)}>
            EDIT
          </Button>

          <Button size="small" color="error" variant="contained" onClick={() => handleDelete(params.row.id)}>
            DELETE
          </Button>
        </Box>
      )
    }
  ];

  const classColumns = [
    { field: 'class_code', headerName: 'Code', flex: 0.7 },
    { field: 'class_name', headerName: 'Class', flex: 1.4 },
    {
      field: 'class_date',
      headerName: 'Beginning',
      flex: 1,
      valueGetter: (_, row = {}) => `${formatDate(row.class_date)} ${formatTimeLabel(row.class_time)}`
    },
    {
      field: 'end_date',
      headerName: 'Ending',
      flex: 1,
      valueGetter: (_, row = {}) => `${formatDate(row.end_date)} ${formatTimeLabel(row.end_time)}`
    },
    { field: 'location', headerName: 'Location', flex: 1 },
    { field: 'student_count', headerName: 'Students', flex: 0.7 },
    { field: 'avg_pre_test_score', headerName: 'Avg Pre', flex: 0.7 },
    { field: 'avg_post_test_score', headerName: 'Avg Post', flex: 0.7 }
  ];

  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    setFieldVisible,
    showSimpleView,
    showFullView,
    resetView
  } = useSavedColumnView('instructors', columns, ['code', 'name', 'active_display', 'phone_display']);

  const {
    columnVisibilityModel: classColumnVisibilityModel,
    setColumnVisibilityModel: setClassColumnVisibilityModel,
    setFieldVisible: setClassFieldVisible,
    showSimpleView: showClassSimpleView,
    showFullView: showClassFullView,
    resetView: resetClassView
  } = useSavedColumnView('instructors-classes', classColumns, ['class_code', 'class_name', 'class_date', 'student_count']);

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h3" gutterBottom>
            Instructors
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage Alamo Center instructors stored in the database.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          Create Instructor
        </Button>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <SavedColumnViewControls
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          setFieldVisible={setFieldVisible}
          showSimpleView={showSimpleView}
          showFullView={showFullView}
          resetView={resetView}
        />
        <Box sx={{ height: 560, width: '100%' }}>
          <DataGrid
            rows={displayRows}
            columns={columns}
            loading={loading}
            rowHeight={60}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
          />
        </Box>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Instructor' : 'Create Instructor'}</DialogTitle>

        <DialogContent>
          <TextField fullWidth margin="normal" label="Code / License Number" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <TextField fullWidth margin="normal" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth margin="normal" label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} />
          <TextField fullWidth margin="normal" label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

          <FormControlLabel
            control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Instructor Details</DialogTitle>

        <DialogContent>
          {selectedInstructor && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">
                  {selectedInstructor.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Code / License #: {selectedInstructor.code || '—'} • Active: {selectedInstructor.active ? 'True' : 'False'}
                </Typography>
              </Box>

              <Divider />

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Phone: ${formatPhone(selectedInstructor.phone) || '—'}`} />
                <Chip label={`Email: ${selectedInstructor.email || '—'}`} />
              </Stack>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Previous Classes Taught
                </Typography>
                <SavedColumnViewControls
                  columns={classColumns}
                  columnVisibilityModel={classColumnVisibilityModel}
                  setFieldVisible={setClassFieldVisible}
                  showSimpleView={showClassSimpleView}
                  showFullView={showClassFullView}
                  resetView={resetClassView}
                  label="Class Columns"
                />
                <Box sx={{ height: 420, width: '100%' }}>
                  <DataGrid
                    rows={instructorClasses.map((item) => ({ ...item, id: item.id }))}
                    columns={classColumns}
                    rowHeight={58}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    columnVisibilityModel={classColumnVisibilityModel}
                    onColumnVisibilityModelChange={setClassColumnVisibilityModel}
                  />
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}